import type { Aggregator, OperatorRow, OperatorWork, Ticket, TicketStatus } from '../domain/types'
import { isInProgress, isPending, isResolved, resolveEligibility } from '../domain/engine'

export function deriveOperatorWorkCountsFromJourneys(
  journeys: Aggregator[],
  serviceConfigIds: number[],
): Pick<OperatorWork, 'eligCountByServiceConfig' | 'eligCountBySession'> {
  const eligCountByServiceConfig = Object.fromEntries(
    serviceConfigIds.map((id) => [id, 0]),
  ) as Record<number, number>
  const eligCountBySession: Record<number, Record<number, number>> = {}

  for (const journey of journeys) {
    if (journey.status !== 'InProgress') {continue}
    for (const ticket of journey.tickets) {
      if (ticket.serviceConfigId == null) {continue}
      if (!isPending(ticket.status)) {continue}
      if (!resolveEligibility(journey.tickets, ticket).podeIniciar) {continue}
      eligCountByServiceConfig[ticket.serviceConfigId] = (eligCountByServiceConfig[ticket.serviceConfigId] ?? 0) + 1
      if (ticket.sessionId != null) {
        const bySession = (eligCountBySession[ticket.serviceConfigId] ||= {})
        bySession[ticket.sessionId] = (bySession[ticket.sessionId] ?? 0) + 1
      }
    }
  }

  return { eligCountByServiceConfig, eligCountBySession }
}

export function reconcileOtherCountByServiceConfig(
  journeys: Aggregator[],
  counts: Pick<OperatorWork, 'eligCountByServiceConfig' | 'otherCountByServiceConfig'>,
): Record<number, number> {
  const unresolvedByServiceConfig: Record<number, number> = {}

  for (const journey of journeys) {
    if (journey.status !== 'InProgress') {continue}
    for (const ticket of journey.tickets) {
      if (ticket.serviceConfigId == null || isResolved(ticket.status)) {continue}
      unresolvedByServiceConfig[ticket.serviceConfigId] = (unresolvedByServiceConfig[ticket.serviceConfigId] ?? 0) + 1
    }
  }

  const otherCountByServiceConfig: Record<number, number> = { ...(counts.otherCountByServiceConfig ?? {}) }
  for (const [serviceConfigId, unresolved] of Object.entries(unresolvedByServiceConfig)) {
    const id = Number(serviceConfigId)
    otherCountByServiceConfig[id] = Math.max(0, unresolved - (counts.eligCountByServiceConfig[id] ?? 0))
  }
  return otherCountByServiceConfig
}

/**
 * Segurança de UI: o painel preserva os estágios anteriores ao check-in para confirmação.
 * O backend continua sendo a autoridade e deve aplicar a mesma regra em call/callnext.
 */
export function filterOperatorWorkByJourneyStatus(work: OperatorWork, journeys: Aggregator[]): OperatorWork {
  const statusById = new Map(journeys.map((journey) => [journey.aggregatorId, journey.status]))
  const isReady = (row: OperatorRow): boolean => row.t.status === 'Opened' || row.t.status === 'Authorized' || row.t.status === 'Reopened' || row.t.status === 'Confirmed' || row.t.status === 'Checked' || isInProgress(row.t.status)
  const allowed = (row: OperatorRow): boolean =>
    !isResolved(row.t.status) && ((statusById.get(row.a.aggregatorId) ?? row.a.status) === 'InProgress' || isReady(row))
  const withStatus = (row: OperatorRow): OperatorRow => ({
    ...row,
    a: { ...row.a, status: statusById.get(row.a.aggregatorId) ?? row.a.status },
  })

  const servingBase = work.serving.filter(allowed).map(withStatus)
  const eligibleBase = work.eligible.filter(allowed).map(withStatus)
  const blocked = work.blocked.filter(allowed).map(withStatus)

  // Segurança de UI: um ticket já chamado/iniciado nunca deve seguir na fila de espera,
  // mesmo que o backend ainda o devolva em `eligible` (ex.: corrida entre call e o refetch).
  const strandedInEligible = eligibleBase.filter((row) => isInProgress(row.t.status))
  const eligible = eligibleBase.filter((row) => !isInProgress(row.t.status))
  const servingIds = new Set(servingBase.map((row) => row.t.ticketId))
  const serving = [...servingBase, ...strandedInEligible.filter((row) => !servingIds.has(row.t.ticketId))]

  const eligCountByServiceConfig = Object.fromEntries(
    Object.keys(work.eligCountByServiceConfig).map((id) => [Number(id), 0]),
  ) as Record<number, number>
  const eligCountBySession: Record<number, Record<number, number>> = {}
  eligible.forEach(({ t }) => {
    if (t.serviceConfigId != null) {
      eligCountByServiceConfig[t.serviceConfigId] = (eligCountByServiceConfig[t.serviceConfigId] ?? 0) + 1
      if (t.sessionId != null) {
        const bySession = (eligCountBySession[t.serviceConfigId] ||= {})
        bySession[t.sessionId] = (bySession[t.sessionId] ?? 0) + 1
      }
    }
  })

  return { serving, eligible, blocked, attendants: work.attendants, eligCountByServiceConfig, otherCountByServiceConfig: work.otherCountByServiceConfig, eligCountBySession }
}

/**
 * Remoção otimista: tira os bilhetes das filas do operador no clique de cancelar
 * (janela de "Desfazer" de 5s ainda roda por baixo — se o undo disparar, o
 * operatorRefreshKey recarrega do servidor e as linhas voltam).
 */
export function removeTicketsFromOperatorWork(work: OperatorWork, ticketIds: Set<number>): OperatorWork {
  if (ticketIds.size === 0) {return work}
  const drop = (row: OperatorRow): boolean => !ticketIds.has(row.t.ticketId)
  const serving = work.serving.filter(drop)
  const eligible = work.eligible.filter(drop)
  const blocked = work.blocked.filter(drop)

  const eligCountByServiceConfig = Object.fromEntries(
    Object.keys(work.eligCountByServiceConfig).map((id) => [Number(id), 0]),
  ) as Record<number, number>
  const eligCountBySession: Record<number, Record<number, number>> = {}
  eligible.forEach(({ t }) => {
    if (t.serviceConfigId != null) {
      eligCountByServiceConfig[t.serviceConfigId] = (eligCountByServiceConfig[t.serviceConfigId] ?? 0) + 1
      if (t.sessionId != null) {
        const bySession = (eligCountBySession[t.serviceConfigId] ||= {})
        bySession[t.sessionId] = (bySession[t.sessionId] ?? 0) + 1
      }
    }
  })

  return { serving, eligible, blocked, attendants: work.attendants, eligCountByServiceConfig, otherCountByServiceConfig: work.otherCountByServiceConfig, eligCountBySession }
}

// View vazia do painel do operador e as contagens derivadas do intervalo consultado.
// Compartilhadas: o App usava os mesmos literais em 4 lugares e uma cópia da view vazia.
export type OperatorWorkCounts = Pick<
  OperatorWork,
  'eligCountByServiceConfig' | 'eligCountBySession' | 'otherCountByServiceConfig'
>

export const EMPTY_OPERATOR_COUNTS: OperatorWorkCounts = {
  eligCountByServiceConfig: {},
  otherCountByServiceConfig: {},
  eligCountBySession: {},
}

export const EMPTY_OPERATOR_WORK: OperatorWork = {
  serving: [],
  eligible: [],
  blocked: [],
  attendants: [],
  ...EMPTY_OPERATOR_COUNTS,
}

// As listas vêm da API e os mapas de contagem do backend: aqui os dois viram uma view só,
// com os mapas por sessão indexados por número (a chave chega como string do JSON).
export function mergeOperatorWorkView(work: OperatorWork, counts: OperatorWorkCounts): OperatorWork {
  const eligCountBySession: Record<number, Record<number, number>> = { ...(work.eligCountBySession ?? {}) }
  for (const [serviceConfigId, bySession] of Object.entries(counts.eligCountBySession ?? {})) {
    eligCountBySession[Number(serviceConfigId)] = {
      ...(eligCountBySession[Number(serviceConfigId)] ?? {}),
      ...bySession,
    }
  }
  return {
    ...work,
    eligCountByServiceConfig: {
      ...work.eligCountByServiceConfig,
      ...counts.eligCountByServiceConfig,
    },
    otherCountByServiceConfig: {
      ...(work.otherCountByServiceConfig ?? {}),
      ...(counts.otherCountByServiceConfig ?? {}),
    },
    eligCountBySession,
  }
}

// Otimismo no painel do operador: Called/Started move a linha pra "serving";
// Completed/Canceled/Unauthorized remove. Confirmed/Reopened têm path explícito
// nos handlers (moveTicketInOperatorWork); outros status ficam pro reload realtime.
export function reconcileOperatorWorkForStatus(prev: OperatorWork, ticketId: number, status: TicketStatus): OperatorWork {
  const existing = [...prev.serving, ...prev.eligible, ...prev.blocked].find((row) => row.t.ticketId === ticketId)
  if (!existing) {return prev}
  if (status === 'Completed' || status === 'Canceled' || status === 'Unauthorized') {
    return removeTicketsFromOperatorWork(prev, new Set([ticketId]))
  }
  if (status === 'Called' || status === 'Started') {
    const t: Ticket = { ...existing.t, status }
    const a: Aggregator = { ...existing.a, tickets: existing.a.tickets.map((x) => x.ticketId === ticketId ? t : x) }
    const row = { ...existing, a, t, info: { podeIniciar: Boolean(t.podeIniciar), motivo: t.motivoBloqueio ?? null, bloqueadoPor: t.bloqueadoPor ?? [], razao: t.razao ?? null } }
    return {
      ...prev,
      serving: [...prev.serving.filter((item) => item.t.ticketId !== ticketId), row],
      eligible: prev.eligible.filter((item) => item.t.ticketId !== ticketId),
      blocked: prev.blocked.filter((item) => item.t.ticketId !== ticketId),
    }
  }
  return prev
}
